import React, { useState } from 'react';
import api from '../../connector/api';

export default function CompleteRegistrationForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [activeParentTab, setActiveParentTab] = useState('mother');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    fullnameKH: '', fullnameEN: '', dob: '', place_birth: '', gender: '', nationality: 'Cambodian',
    apply_grade: '', previous_school: '', 
    why_choose_cais: '', health_record: '', physical_checkup_acknowledged: false,
    motherNameKH: '', motherNameEN: '', motherContact: '', motherOccupation: '', motherReligion: '',
    fatherNameKH: '', fatherNameEN: '', fatherContact: '', fatherOccupation: '', fatherReligion: '',
    address: '', contactNumber: '', emergency_firstName: '', emergency_lastName: '', emergency_address: '',
    exam_policy_acknowledged: false, truth_statement_acknowledged: false
  });

  // --- Track Dynamic Uploaded Files ---
  const [uploadedDocuments, setUploadedDocuments] = useState([
    { 
      document_type: 'BIRTH_CERTIFICATE', 
      file: null, 
      labelKH: 'សំបុត្រកំណើត',
      labelEN: 'Birth Certificate', 
      required: true
    },
    { 
      document_type: 'TRANSCRIPT', 
      file: null, 
      labelKH: 'ព្រឹត្តិបត្រពិន្ទុ ឬសៀវភៅតាមដាន',
      labelEN: 'Transcript or Report Card', 
      required: true
    },
    { 
      document_type: 'PHOTO', 
      file: null, 
      labelKH: 'រូបថតសិស្ស (៤x៦)',
      labelEN: 'Student Passport Photo', 
      required: true
    }
  ]);
  
  const [errors, setErrors] = useState({});

  const requiredFieldsByStep = {
    1: ['fullnameKH', 'fullnameEN', 'dob', 'place_birth', 'gender', 'nationality'],
    2: ['apply_grade', 'why_choose_cais', 'physical_checkup_acknowledged'], 
    3: ['motherNameKH', 'motherNameEN', 'motherContact', 'fatherNameKH', 'fatherNameEN', 'fatherContact'],
    4: ['address', 'contactNumber', 'emergency_firstName', 'emergency_lastName', 'emergency_address', 'exam_policy_acknowledged', 'truth_statement_acknowledged']
  };

  // Helper logic to figure out if transcripts should be omitted
  const isEarlyChildhood = ['Pre-School', 'Pre-Kindergarten', 'Kindergarten'].includes(formData.apply_grade);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (type === 'checkbox' ? checked : value.trim() !== '') {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleFileChange = (index, file) => {
    const updated = [...uploadedDocuments];
    updated[index].file = file;
    setUploadedDocuments(updated);

    if (file) {
      setErrors(prev => ({ ...prev, [`doc_${index}`]: false }));
    }
  };

  const validateCurrentStep = () => {
    const fieldsToValidate = requiredFieldsByStep[currentStep];
    const newErrors = {};
    let isStepValid = true;

    fieldsToValidate.forEach(field => {
      const value = formData[field];
      if (typeof value === 'boolean') {
        if (!value) {
          newErrors[field] = true;
          isStepValid = false;
        }
      } else if (!value || value.trim() === '') {
        newErrors[field] = true;
        isStepValid = false;
      }
    });

    // Dynamic Step 2 Document Rules Engine
    if (currentStep === 2) {
      uploadedDocuments.forEach((doc, index) => {
        const isTranscript = doc.document_type === 'TRANSCRIPT';
        
        // Skip validation tracking if it's a hidden transcript card
        if (isTranscript && isEarlyChildhood) return;

        if (doc.required && !doc.file) {
          newErrors[`doc_${index}`] = true;
          isStepValid = false;
        }
      });
    }

    setErrors(newErrors);

    if (currentStep === 3 && !isStepValid) {
      const motherHasErrors = ['motherNameKH', 'motherNameEN', 'motherContact'].some(f => newErrors[f]);
      const fatherHasErrors = ['fatherNameKH', 'fatherNameEN', 'fatherContact'].some(f => newErrors[f]);
      
      if (motherHasErrors) {
        setActiveParentTab('mother');
      } else if (fatherHasErrors) {
        setActiveParentTab('father');
      }
    }

    return isStepValid;
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setErrors({});
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateCurrentStep()) return;

    setIsSubmitting(true);
    const payload = new FormData();

    // 1. Pack standard textual properties 
    Object.keys(formData).forEach(key => {
      payload.append(key, formData[key]);
    });

    // 2. Pack file uploads correctly using consistent keys
    uploadedDocuments.forEach((doc) => {
      const isTranscript = doc.document_type === 'TRANSCRIPT';
      
      // Do not bundle the payload item structure if transcript is unneeded
      if (isTranscript && isEarlyChildhood) return;

      if (doc.file) {
        payload.append('documents', doc.file);
        payload.append('document_types', doc.document_type);
      }
    });

    try {
      const response = await api.post('/register-student/', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const result = response.data;

      if (result.success || response.status === 201) {
        setSubmissionSuccess(true);
        window.scrollTo(0, 0);
      } else {
        if (result.errors) {
          setErrors(result.errors);
          alert('ការដាក់ពាក្យត្រូវបានបដិសេធ។ / Submission rejected by server.');
        } else {
          alert('មានបញ្ហាបច្ចេកទេសមិនស្គាល់មួយបានកើតឡើង។ / An unknown communication problem occurred.');
        }
      }
    } catch (error) {
      console.error('Submission processing failure:', error);
      if (error.response && error.response.data) {
        const serverErrors = error.response.data.errors || error.response.data;
        setErrors(serverErrors);
        alert('ការដាក់ពាក្យត្រូវបានបដិសេធ។ សូមពិនិត្យកំហុសដែលបានបង្ហាញ។ / Submission rejected by server.');
      } else {
        alert('ការបញ្ជូនទិន្នន័យបានបរាជ័យ។ / Network transmission failed.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = (fieldName, isKhmer = false) => `w-full px-3.5 sm:px-4 py-2.5 sm:py-3 rounded-xl border outline-none transition-all text-xs sm:text-sm ${
    isKhmer ? 'font-dynamic-serif' : 'font-dynamic-title'
  } ${
    errors[fieldName] 
      ? 'border-red-500 bg-red-50/30 focus:ring-2 focus:ring-red-200' 
      : 'border-slate-300/80 focus:ring-2 focus:ring-[#002855]/20 focus:border-[#002855]'
  }`;

  const checkboxCardStyle = (fieldName) => `flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border transition-all text-xs sm:text-sm cursor-pointer ${
    errors[fieldName] ? 'border-red-500 bg-red-50/40 shadow-sm ring-1 ring-red-200' : 'border-slate-200 bg-slate-50/50 hover:bg-slate-50'
  }`;

  if (submissionSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col justify-center items-center p-4 sm:p-6">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-8 text-center space-y-6">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-3xl mx-auto shadow-sm">✓</div>
          <div className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 font-dynamic-serif">ទទួលបានពាក្យសុំដោយជោគជ័យ!</h2>
            <h3 className="text-base sm:text-lg font-bold text-slate-600 font-dynamic-title">Application Received Successfully!</h3>
            <p className="text-xs sm:text-sm font-semibold text-[#002855] uppercase tracking-wider font-dynamic-serif">សាលាអន្តរជាតិ អាត់វេនទីស្ទ កម្ពុជា ​​(CAIS)</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 sm:p-5 text-left text-xs leading-relaxed space-y-4 text-slate-600">
            <p className="font-bold text-slate-900 border-b border-slate-200 pb-1.5 font-dynamic-serif">
              ជំហានបន្ទាប់ដែលត្រូវធ្វើ <span className="font-dynamic-title font-normal text-slate-500">/ Next Step Processing Milestones:</span>
            </p>

            <div className="space-y-1">
              <p className="font-semibold text-slate-900 font-dynamic-serif">១. សូមព្រីន ឬថតរូបភាពនៃសារជូនដំណឹងនេះទុកលើទូរស័ព្ទរបស់អ្នក។</p>
              <p className="text-slate-500 pl-4 font-dynamic-title">1. Please print or keep a screenshot of this notification on your device.</p>
            </div>

            <div className="space-y-1">
              <p className="font-semibold text-slate-900 font-dynamic-serif">
                ២. សូមអញ្ជើញមកកាន់ការិយាល័យរដ្ឋបាលដើម្បីបង់ថ្លៃប្រឡងចូលចំនួន <strong className="text-red-600 font-dynamic-title">$៣០ ដុល្លារអាមេរិក</strong> ឬបង្ហាញបង្កាន់ដៃបង់ប្រាក់ដែលមានស្រាប់។
              </p>
              <p className="text-slate-500 pl-4 font-dynamic-title">
                2. Visit the administrative admissions window to settle the <strong>$30 USD Entrance Examination fee</strong> or present your existing payment receipt voucher.
              </p>
            </div>

            <div className="space-y-1">
              <p className="font-semibold text-slate-900 font-dynamic-serif">
                ៣. ខាងរដ្ឋបាលសាលានឹងទាក់ទងទៅកាន់លេខទូរស័ព្ទរបស់អ្នក (<strong className="font-dynamic-title">{formData.contactNumber}</strong>) ដើម្បីណាត់ជួបសម្រាប់ការធ្វើតេស្តសមត្ថភាពភាសាអង់គ្លេស និងភាសាខ្មែរ បន្ទាប់ពីការផ្ទៀងផ្ទាត់រួចរាល់។
              </p>
              <p className="text-slate-500 pl-4 font-dynamic-title">
                3. Our academic council registrar will contact you at your listed number (<strong>{formData.contactNumber}</strong>) to lock down the English and Khmer proficiency testing session timings once verified.
              </p>
            </div>
          </div>
          <button onClick={() => window.location.reload()} className="w-full py-3 bg-[#002855] text-white rounded-xl font-bold text-xs sm:text-sm shadow transition-all hover:bg-[#002855]/90">
            <span className="font-dynamic-serif">ដាក់ពាក្យសុំម្តងទៀត</span> / <span className="font-dynamic-title">Submit Another Registry Entry</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 antialiased flex flex-col justify-between">
      <header className="bg-[#002855]/90 backdrop-blur-md border-b border-[#002855]/20 py-3.5 px-4 sm:px-6 sticky top-0 z-50 shadow-md">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-base sm:text-lg font-black tracking-tight text-white font-dynamic-title">CAIS <span className="text-slate-300 font-normal">NEW STUDENT REGISTRATION</span></h1>
            <p className="text-[11px] sm:text-xs text-slate-200 font-medium">
              <span className="font-dynamic-serif">ទម្រង់បែបបទចុះឈ្មោះសិស្សថ្មី ២០២៦-២០២៧</span> / <span className="font-dynamic-title">New Student Application Form 2026-2027</span>
            </p>
          </div>
        </div>
      </header>

      <main className="flex-grow p-3 sm:p-6 md:p-8 flex items-start justify-center">
        <div className="w-full max-w-6xl bg-white rounded-xl sm:rounded-2xl shadow-md border border-slate-200 overflow-hidden">
          
          {/* Responsive Progress Stepper Header */}
          <div className="bg-slate-50 border-b border-slate-200 p-4 sm:p-6 md:px-10 overflow-x-auto">
            <div className="flex justify-between items-center min-w-[500px] md:min-w-0 gap-2 sm:gap-4">
              {[
                { idx: 1, labelEN: 'Student', labelKH: 'ព័ត៌មានសិស្ស' },
                { idx: 2, labelEN: 'Academic & Docs', labelKH: 'ការសិក្សា & ឯកសារ' },
                { idx: 3, labelEN: 'Parents', labelKH: 'ព័ត៌មានមាតាបិតា' },
                { idx: 4, labelEN: 'Terms & Submit', labelKH: 'លក្ខខណ្ឌ & ដាក់ពាក្យ' }
              ].map((step) => (
                <div key={step.idx} className="flex items-center gap-2 sm:gap-3 flex-1 last:flex-none">
                  <div className="flex items-center gap-2">
                    <span className={`w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center font-bold text-xs sm:text-sm transition-all duration-300 ${
                      currentStep === step.idx 
                        ? 'bg-[#002855] text-white shadow-md shadow-[#002855]/30' 
                        : currentStep > step.idx ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {currentStep > step.idx ? '✓' : step.idx}
                    </span>
                    <div className="flex flex-col text-left">
                      <span className={`text-[10px] sm:text-[11px] font-dynamic-serif font-bold leading-tight ${currentStep === step.idx ? 'text-slate-900' : 'text-slate-400'}`}>{step.labelKH}</span>
                      <span className={`text-[9px] sm:text-[10px] font-dynamic-title font-medium leading-none ${currentStep === step.idx ? 'text-slate-700' : 'text-slate-400'}`}>{step.labelEN}</span>
                    </div>
                  </div>
                  {step.idx < 4 && <div className={`h-0.5 flex-grow hidden lg:block rounded-full ${currentStep > step.idx ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>}
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-4 sm:p-6 md:p-10 space-y-6 sm:space-y-8">
            
            {/* STEP 1: STUDENT PROFILE */}
            {currentStep === 1 && (
              <div className="space-y-5 sm:space-y-6">
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-900">
                    <span className="font-dynamic-serif">ប្រវត្តិរូបសិស្ស</span> / <span className="font-dynamic-title">Student Profile Demographics</span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="font-dynamic-serif">ឈ្មោះពេញ (ភាសាខ្មែរ)</span> / <span className="font-dynamic-title">Full Name (Khmer) *</span>
                    </label>
                    <input type="text" name="fullnameKH" value={formData.fullnameKH} onChange={handleInputChange} className={inputStyle('fullnameKH', true)} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="font-dynamic-serif">ឈ្មោះពេញ (អក្សរឡាតាំង)</span> / <span className="font-dynamic-title">Full Name (English) *</span>
                    </label>
                    <input type="text" name="fullnameEN" value={formData.fullnameEN} onChange={handleInputChange} className={`${inputStyle('fullnameEN', false)} uppercase`} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="font-dynamic-serif">ថ្ងៃខែឆ្នាំកំណើត</span> / <span className="font-dynamic-title">Date of Birth *</span>
                    </label>
                    <input type="date" name="dob" value={formData.dob} onChange={handleInputChange} className={inputStyle('dob', false)} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="font-dynamic-serif">ភេទ</span> / <span className="font-dynamic-title">Gender *</span>
                    </label>
                    <select name="gender" value={formData.gender} onChange={handleInputChange} className={inputStyle('gender', true)}>
                      <option value="">-- ជ្រើសរើសភេទ --</option>
                      <option value="Male">ប្រុស / Male</option>
                      <option value="Female">ស្រី / Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="font-dynamic-serif">សញ្ជាតិ</span> / <span className="font-dynamic-title">Nationality *</span>
                    </label>
                    <input type="text" name="nationality" value={formData.nationality} onChange={handleInputChange} className={inputStyle('nationality', true)} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="font-dynamic-serif">ទីកន្លែងកំណើត</span> / <span className="font-dynamic-title">Place of Birth *</span>
                    </label>
                    <input type="text" name="place_birth" value={formData.place_birth} onChange={handleInputChange} className={inputStyle('place_birth', true)} />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2: ACADEMIC PLACEMENT, HEALTH RECORDS & DOCUMENT UPLOADS */}
            {currentStep === 2 && (
              <div className="space-y-5 sm:space-y-6">
                <div>
                  <h2 className="text-base sm:text-xl font-bold text-slate-900">
                    <span className="font-dynamic-serif">តម្រូវការផ្នែកសិក្សា និងវេជ្ជសាស្ត្រ</span> / <span className="font-dynamic-title">Academic & Medical Requirements</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="font-dynamic-serif">សូមផ្តល់ព័ត៌មានអំពីថ្នាក់សិក្សា និងប្រវត្តសុខភាពរបស់សិស្ស។</span> / <span className="font-dynamic-title">Please provide medical feedback parameters alongside grade entry applications.</span>
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:gap-6">
                  <div className="w-full md:w-1/2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="font-dynamic-serif">ពាក្យសុំចូលរៀនថ្នាក់ទី</span> / <span className="font-dynamic-title">Apply Grade Level *</span>
                    </label>
                    <select name="apply_grade" value={formData.apply_grade} onChange={handleInputChange} className={`${inputStyle('apply_grade', true)} bg-white`}>
                      <option value="">-- ចុចដើម្បីជ្រើសរើសថ្នាក់ / Click to select grade --</option>
                      <optgroup label="ថ្នាក់ត្រៀម / Prepare for School">
                        <option value="Pre-School">ថ្នាក់មុនមត្តេយ្យ / Pre-School</option>
                      </optgroup>
                      <optgroup label="បឋមសិក្សា និងអនុវិទ្យាល័យ / Elementary & Middle School">
                        <option value="Pre-Kindergarten">ថ្នាក់មត្តេយ្យកម្រិតទាប / Pre-Kindergarten</option>
                        <option value="Kindergarten">ថ្នាក់មត្តេយ្យ / Kindergarten</option>
                        <option value="Grade 1">ថ្នាក់ទី ១ / Grade 1</option>
                        <option value="Grade 2">ថ្នាក់ទី ២ / Grade 2</option>
                        <option value="Grade 3">ថ្នាក់ទី ៣ / Grade 3</option>
                        <option value="Grade 4">ថ្នាក់ទី ៤ / Grade 4</option>
                        <option value="Grade 5">ថ្នាក់ទី ៥ / Grade 5</option>
                        <option value="Grade 6">ថ្នាក់ទី ៦ / Grade 6</option>
                        <option value="Grade 7">ថ្នាក់ទី ៧ / Grade 7</option>
                        <option value="Grade 8">ថ្នាក់ទី ៨ / Grade 8</option>
                      </optgroup>
                      <optgroup label="វិទ្យាល័យ / High School">
                        <option value="Grade 9">ថ្នាក់ទី ៩ / Grade 9</option>
                        <option value="Grade 10">ថ្នាក់ទី ១០ / Grade 10</option>
                        <option value="Grade 11">ថ្នាក់ទី ១១ / Grade 11</option>
                        <option value="Grade 12">ថ្នាក់ទី ១២ / Grade 12</option>
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="font-dynamic-serif">សាលារៀនចាស់</span> / <span className="font-dynamic-title">Previous School Attended</span>
                    </label>
                    <input type="text" name="previous_school" placeholder="ឈ្មោះសាលារៀនចាស់ / Name of institution" value={formData.previous_school} onChange={handleInputChange} className={inputStyle('previous_school', true)} />
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="block font-bold">
                        <span className="font-dynamic-serif">ហេតុអ្វីបានជាលោកអ្នកជ្រើសរើសសាលា CAIS? *</span> / <span className="font-dynamic-title">Why choose CAIS? *</span>
                      </span>
                    </label>
                    <textarea name="why_choose_cais" placeholder="សូមសរសេរមូលហេតុរបស់អ្នក... / Please write your reason..." value={formData.why_choose_cais} onChange={handleInputChange} rows="2" className={inputStyle('why_choose_cais', true)}></textarea>
                  </div>

                  {/* Document Upload Matrix */}
                  <div className="p-4 sm:p-5 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
                    <h3 className="text-xs font-bold tracking-wider text-slate-800 uppercase">
                      <span className="font-dynamic-serif">ឯកសារយោង</span> / <span className="font-dynamic-title">Upload Supporting Documents</span>
                    </h3>
                    <p className="text-[11px] text-slate-500 leading-tight">
                      <span className="font-dynamic-serif">សូមភ្ជាប់ឯកសារទម្រង់ជា PDF ឬរូបភាពថតច្បាស់ (PNG, JPEG) ទំហំមិនហួសពី 5MB។</span>
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
                      {uploadedDocuments.map((doc, index) => {
                        const isTranscript = doc.document_type === 'TRANSCRIPT';
                        if (isTranscript && isEarlyChildhood) return null;

                        return (
                          <div key={doc.document_type} className={`p-3 rounded-xl bg-white border flex flex-col justify-between space-y-2 transition-all ${errors[`doc_${index}`] ? 'border-red-500 bg-red-50/20' : 'border-slate-200'}`}>
                            <label className="text-[11px] font-bold text-slate-700 leading-tight">
                              <span className="font-dynamic-serif">{doc.labelKH}</span> / <span className="font-dynamic-title">{doc.labelEN}</span> {doc.required && <span className="text-red-500 font-black">*</span>}
                            </label>

                            <input 
                              type="file" 
                              accept=".pdf,.png,.jpg,.jpeg"
                              onChange={(e) => handleFileChange(index, e.target.files[0] || null)}
                              className="text-xs file:mr-2 sm:file:mr-3 file:py-1.5 file:px-2.5 sm:file:px-3 file:rounded-lg file:border-0 file:text-[11px] sm:file:text-xs file:font-semibold file:bg-[#002855]/10 file:text-[#002855] hover:file:bg-[#002855]/20 cursor-pointer w-full font-dynamic-title"
                            />
                            {doc.file && <span className="text-[10px] text-emerald-600 font-semibold truncate font-dynamic-title">✓ {doc.file.name}</span>}
                            {errors[`doc_${index}`] && <span className="text-[10px] text-red-500 font-medium font-dynamic-serif">សូមជ្រើសរើសឯកសារនេះ / This file is mandatory</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="block font-bold">
                        <span className="font-dynamic-serif">កំណត់ត្រាសុខភាពរបស់សិស្ស</span> / <span className="font-dynamic-title">Student's Health Record</span>
                      </span>
                    </label>
                    <textarea name="health_record" value={formData.health_record} placeholder="រៀបរាប់ពីបញ្ហាប្រតិកម្មអាឡែស៊ី ឬជំងឺប្រចាំកាយ (បើមាន) / List details regarding allergies, current medication or chronic conditions (Optional)" onChange={handleInputChange} rows="2" className={inputStyle('health_record', true)}></textarea>
                  </div>

                  <label className={checkboxCardStyle('physical_checkup_acknowledged')}>
                    <input type="checkbox" name="physical_checkup_acknowledged" checked={formData.physical_checkup_acknowledged} onChange={handleInputChange} className="mt-1 h-4 w-4 rounded text-[#002855] focus:ring-[#002855]" />
                    <div className="space-y-1">
                      <p className="font-bold text-slate-900">
                        <span className="font-dynamic-serif">តម្រូវឱ្យមានការពិនិត្យសុខភាពសិស្ស</span> / <span className="font-dynamic-title">Student's Physical Check-up Required *</span>
                      </p>
                      <p className="text-xs text-[#002855] font-semibold leading-relaxed font-dynamic-serif">
                        សូមផ្ញើជូនលទ្ធផលតេស្តពិនិត្យសុខភាពកូនរបស់អ្នកមកកាន់ការិយាល័យ។ ឯកសារត្រូវមានហត្ថលេខារបស់គ្រូពេទ្យ ព្រមទាំងត្រានិងកាលបរិច្ឆេទរបស់មន្ទីរពេទ្យច្បាស់លាស់។
                      </p>
                      <p className="text-xs text-slate-600 leading-relaxed font-dynamic-title">
                        Please submit to the office a complete physical check-up of your child with the physician's signature and hospital's seal and date.
                      </p> 
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* STEP 3: PARENTS PROFILES */}
            {currentStep === 3 && (
              <div className="space-y-5 sm:space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-100 pb-4">
                  <div>
                    <h2 className="text-base sm:text-xl font-bold text-slate-900">
                      <span className="font-dynamic-serif">ព័ត៌មានមាតាបិតា</span> / <span className="font-dynamic-title">Parent Profiles</span>
                    </h2>
                  </div>
                  <div className="bg-slate-100 p-1 rounded-xl border border-slate-200 flex gap-1 w-full sm:w-auto">
                    <button type="button" onClick={() => setActiveParentTab('mother')} className={`px-3 sm:px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase transition-all flex-1 sm:flex-none relative ${activeParentTab === 'mother' ? 'bg-white text-[#002855] shadow-sm font-bold' : 'text-slate-500'}`}>
                      <span className="font-dynamic-serif">👩 ម្តាយ</span> / <span className="font-dynamic-title">Mother</span>
                      {(errors.motherNameKH || errors.motherNameEN || errors.motherContact) && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></span>}
                    </button>
                    <button type="button" onClick={() => setActiveParentTab('father')} className={`px-3 sm:px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase transition-all flex-1 sm:flex-none relative ${activeParentTab === 'father' ? 'bg-white text-[#002855] shadow-sm font-bold' : 'text-slate-500'}`}>
                      <span className="font-dynamic-serif">👨 ឪពុក</span> / <span className="font-dynamic-title">Father</span>
                      {(errors.fatherNameKH || errors.fatherNameEN || errors.fatherContact) && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></span>}
                    </button>
                  </div>
                </div>

                {activeParentTab === 'mother' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 bg-slate-50/50 p-4 sm:p-6 rounded-2xl border border-slate-100">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        <span className="font-dynamic-serif">ឈ្មោះមាតា (ភាសាខ្មែរ)</span> / <span className="font-dynamic-title">Mother Name (Khmer) *</span>
                      </label>
                      <input type="text" name="motherNameKH" value={formData.motherNameKH} placeholder="ឈ្មោះមាតា" onChange={handleInputChange} className={inputStyle('motherNameKH', true)} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        <span className="font-dynamic-serif">ឈ្មោះមាតា (អក្សរឡាតាំង)</span> / <span className="font-dynamic-title">Mother Name (English) *</span>
                      </label>
                      <input type="text" name="motherNameEN" value={formData.motherNameEN} placeholder="Mother Full Name" onChange={handleInputChange} className={`${inputStyle('motherNameEN', false)} uppercase`} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        <span className="font-dynamic-serif">លេខទូរស័ព្ទទំនាក់ទំនង</span> / <span className="font-dynamic-title">Contact Phone *</span>
                      </label>
                      <input type="tel" name="motherContact" value={formData.motherContact} placeholder="Contact Phone" onChange={handleInputChange} className={inputStyle('motherContact', false)} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        <span className="font-dynamic-serif">មុខរបរ</span> / <span className="font-dynamic-title">Occupation</span>
                      </label>
                      <input type="text" name="motherOccupation" value={formData.motherOccupation} placeholder="Occupation" onChange={handleInputChange} className={inputStyle('motherOccupation', true)} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        <span className="font-dynamic-serif">សាសនា</span> / <span className="font-dynamic-title">Religion</span>
                      </label>
                      <input type="text" name="motherReligion" value={formData.motherReligion} placeholder="Religion" onChange={handleInputChange} className={inputStyle('motherReligion', true)} />
                    </div>
                  </div>
                )}

                {activeParentTab === 'father' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 bg-slate-50/50 p-4 sm:p-6 rounded-2xl border border-slate-100">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        <span className="font-dynamic-serif">ឈ្មោះបិតា (ភាសាខ្មែរ)</span> / <span className="font-dynamic-title">Father Name (Khmer) *</span>
                      </label>
                      <input type="text" name="fatherNameKH" value={formData.fatherNameKH} placeholder="ឈ្មោះបិតា" onChange={handleInputChange} className={inputStyle('fatherNameKH', true)} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        <span className="font-dynamic-serif">ឈ្មោះបិតា (អក្សរឡាតាំង)</span> / <span className="font-dynamic-title">Father Name (English) *</span>
                      </label>
                      <input type="text" name="fatherNameEN" value={formData.fatherNameEN} placeholder="Father Full Name" onChange={handleInputChange} className={`${inputStyle('fatherNameEN', false)} uppercase`} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        <span className="font-dynamic-serif">លេខទូរស័ព្ទទំនាក់ទំនង</span> / <span className="font-dynamic-title">Contact Phone *</span>
                      </label>
                      <input type="tel" name="fatherContact" value={formData.fatherContact} placeholder="Contact Phone" onChange={handleInputChange} className={inputStyle('fatherContact', false)} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        <span className="font-dynamic-serif">មុខរបរ</span> / <span className="font-dynamic-title">Occupation</span>
                      </label>
                      <input type="text" name="fatherOccupation" value={formData.fatherOccupation} placeholder="Occupation" onChange={handleInputChange} className={inputStyle('fatherOccupation', true)} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        <span className="font-dynamic-serif">សាសនា</span> / <span className="font-dynamic-title">Religion</span>
                      </label>
                      <input type="text" name="fatherReligion" value={formData.fatherReligion} placeholder="Religion" onChange={handleInputChange} className={inputStyle('fatherReligion', true)} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 4: LEGAL STATEMENT & SUBMISSION */}
            {currentStep === 4 && (
              <div className="space-y-5 sm:space-y-6">
                <div>
                  <h2 className="text-base sm:text-xl font-bold text-slate-900">
                    <span className="font-dynamic-serif">កិច្ចព្រមព្រៀងតាមផ្លូវច្បាប់</span> / <span className="font-dynamic-title">Legal Agreements</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="font-dynamic-serif">សូមអាន និងបញ្ជាក់ពីការយល់ព្រមលើលក្ខខណ្ឌខាងក្រោម។</span> / <span className="font-dynamic-title">Please read and confirm the entrance screening terms below.</span>
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="font-dynamic-serif">លេខទូរស័ព្ទចម្បងរបស់គ្រួសារ</span> / <span className="font-dynamic-title">Primary Family Contact Phone *</span>
                    </label>
                    <input type="tel" name="contactNumber" value={formData.contactNumber} placeholder="012345678" onChange={handleInputChange} className={inputStyle('contactNumber', false)} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 sm:mb-2">
                      <span className="font-dynamic-serif">អាសយដ្ឋានបច្ចុប្បន្ន</span> / <span className="font-dynamic-title">Residential Living Address *</span>
                    </label>
                    <textarea name="address" value={formData.address} placeholder="ផ្ទះលេខ ផ្លូវ សង្កាត់ ខណ្ឌ..." onChange={handleInputChange} rows="2" className={inputStyle('address', true)}></textarea>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <h4 className="text-xs font-bold tracking-wider text-slate-700 sm:col-span-2">
                      🚨 <span className="font-dynamic-serif">ទំនាក់ទំនងក្នុងករណីអាសន្ន</span> / <span className="font-dynamic-title">Emergency Contact Details</span>
                    </h4>
                    <div>
                      <input type="text" name="emergency_firstName" value={formData.emergency_firstName} onChange={handleInputChange} placeholder="នាមខ្លួន / First Name *" className={inputStyle('emergency_firstName', true)} />
                    </div>
                    <div>
                      <input type="text" name="emergency_lastName" value={formData.emergency_lastName} onChange={handleInputChange} placeholder="នាមត្រកូល / Last Name *" className={inputStyle('emergency_lastName', true)} />
                    </div>
                    <input type="text" name="emergency_address" value={formData.emergency_address} onChange={handleInputChange} placeholder="អាសយដ្ឋានសង្គ្រោះបន្ទាន់ / Emergency Address *" className={`${inputStyle('emergency_address', true)} sm:col-span-2`} />
                  </div>

                  <label className={checkboxCardStyle('exam_policy_acknowledged')}>
                    <input type="checkbox" name="exam_policy_acknowledged" checked={formData.exam_policy_acknowledged} onChange={handleInputChange} className="mt-1 h-4 w-4 rounded text-[#002855] focus:ring-[#002855]" />
                    <div className="space-y-1.5">
                      <p className="font-bold text-slate-900 text-xs sm:text-sm">
                        <span className="font-dynamic-serif">កិច្ចព្រមព្រៀងគោលការណ៍ប្រឡងចូល និងបង់ថ្លៃសេវា</span> / <span className="font-dynamic-title">Entrance Exam Policy *</span>
                      </p>
                      <p className="text-[#002855] font-semibold leading-relaxed text-xs font-dynamic-serif">
                        ខ្ញុំយល់ថាកូនរបស់ខ្ញុំនឹងត្រូវតម្រូវឱ្យប្រឡងចូលដើម្បីវាស់ស្ទង់សមត្ថភាពភាសាអង់គ្លេស និងភាសាខ្មែរ (ខាងសាលានឹងទាក់ទងហៅទៅអ្នកតាមក្រោយ) ដើម្បីកំណត់ថ្នាក់សិក្សារបស់ពួកគេនៅ CAIS។ តម្លៃនៃការប្រឡងនេះគឺ ៣០ ដុល្លារអាមេរិក ដែលខ្ញុំត្រូវបង់មុនពេលពួកគេប្រឡង ហើយថវិកានេះមិនអាចដកវិញបានទេ។ (សូមបង់ប្រាក់ ឬបង្ហាញបង្កាន់ដៃទៅការិយាល័យ)។
                      </p>
                      <p className="text-slate-500 leading-relaxed text-xs font-dynamic-title">
                        I understand my child will be required to take an Entrance Exam - English/Khmer Proficiency test (Schedule: We will call you) which will be used to determine their grade placement at CAIS. The cost of this exam will be $30 USD which I will need to pay before they take it. It's non-refundable. (Pay or show receipt to the office).
                      </p>
                    </div>
                  </label>

                  <label className={checkboxCardStyle('truth_statement_acknowledged')}>
                    <input type="checkbox" name="truth_statement_acknowledged" checked={formData.truth_statement_acknowledged} onChange={handleInputChange} className="mt-1 h-4 w-4 rounded text-[#002855] focus:ring-[#002855]" />
                    <div className="space-y-1.5">
                      <p className="font-bold text-slate-900 text-xs sm:text-sm">
                        <span className="font-dynamic-serif">ការធានាអះអាងសច្ចភាពទិន្នន័យ</span> / <span className="font-dynamic-title">Truth & Accuracy Statement *</span>
                      </p>
                      <p className="text-[#002855] font-semibold text-xs font-dynamic-serif">ខ្ញុំសូមបញ្ជាក់ថា ព័ត៌មានដែលបានផ្តល់ជូននៅក្នុងសំណុំបែបបទនេះគឺត្រឹមត្រូវ និងជាការពិតទាំងស្រុង។</p>
                      <p className="text-slate-500 text-xs font-dynamic-title">The information provided in this application is true and honest.</p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Responsive Action Buttons */}
            <div className="flex flex-col-reverse sm:flex-row justify-between items-center gap-3 pt-6 border-t border-slate-100">
              <button 
                type="button" 
                onClick={prevStep} 
                disabled={currentStep === 1 || isSubmitting} 
                className={`w-full sm:w-auto px-5 py-2.5 rounded-xl text-xs font-bold border transition-all ${
                  currentStep === 1 
                    ? 'border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50' 
                    : 'border-slate-300 hover:bg-slate-50 text-slate-700'
                }`}
              >
                <span className="font-dynamic-serif">← ថយក្រោយ</span> / <span className="font-dynamic-title">Previous</span>
              </button>

              {currentStep < 4 ? (
                <button type="button" onClick={nextStep} className="w-full sm:w-auto px-6 py-2.5 bg-[#002855] text-white rounded-xl font-bold text-xs shadow-md shadow-[#002855]/20 hover:bg-[#002855]/90 transition-all">
                  <span className="font-dynamic-serif">បន្តទៅមុខ</span> / <span className="font-dynamic-title">Next Step →</span>
                </button>
              ) : (
                <button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className={`w-full sm:w-auto px-8 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-xs shadow-md shadow-emerald-600/20 hover:bg-emerald-700 transition-all ${
                    isSubmitting ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <span className="font-dynamic-serif">កំពុងបញ្ជូន...</span> / <span className="font-dynamic-title">Submitting...</span>
                    </>
                  ) : (
                    <>
                      🚀 <span className="font-dynamic-serif">ដាក់ពាក្យសុំចុះឈ្មោះ</span> / <span className="font-dynamic-title">Submit Application</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}